/**
 * Diretório oficial de mobilidade — lógica pura (sem IA, sem Maps, sem rede).
 *
 * Regras:
 * - Só entram URLs https de fontes oficiais (operador, autoridade de transporte
 *   ou município). Nunca se inventam links: se não existir recurso curado para
 *   a cidade, mostra-se um fallback genérico claramente identificado.
 * - A resolução é determinística por chave de cidade normalizada.
 */

export const MOBILITY_MODES = [
  "transit_pass",
  "transit_tickets",
  "car_rental",
  "scooter",
  "bike_share",
  "parking",
  "tolls",
  "low_emission_zone",
  "transfer",
  "taxi_rideshare",
  "ferry",
] as const;

export type MobilityMode = (typeof MOBILITY_MODES)[number];

export type MobilityResource = {
  id: string;
  cityKey: string;
  cityLabel: string;
  countryCode: string | null;
  mode: MobilityMode;
  providerName: string;
  url: string;
  official: boolean;
  language: string | null;
  notes: string | null;
  priority: number;
  active: boolean;
  verifiedAt: string | null;
};

export const MODE_LABEL: Record<MobilityMode, string> = {
  transit_pass: "Passes de transporte",
  transit_tickets: "Bilhetes de transporte",
  car_rental: "Aluguer de carro",
  scooter: "Trotinetes",
  bike_share: "Bicicletas partilhadas",
  parking: "Estacionamento",
  tolls: "Portagens",
  low_emission_zone: "Zonas de emissões / ZTL",
  transfer: "Transfers",
  taxi_rideshare: "Táxis e TVDE",
  ferry: "Ferries",
};

/** Avisos essenciais mostrados a todos os planos (nunca escondidos atrás do Pro). */
export const MODE_WARNING: Partial<Record<MobilityMode, string>> = {
  low_emission_zone:
    "Confirma restrições de circulação (ZTL, ZBE, ULEZ, Crit'Air) antes de entrar no centro — as coimas são automáticas.",
  tolls: "Algumas autoestradas só aceitam pagamento eletrónico; confirma antes de circular.",
  parking: "Verifica horários e zonas pagas; muitos centros históricos são de acesso condicionado.",
  scooter: "Idade mínima, capacete e zonas proibidas variam por cidade.",
  car_rental: "Confirma carta de condução aceite, caução e seguro antes de reservar.",
};

/** Normaliza o nome da cidade para a chave usada no diretório. */
export function cityKey(city: string): string {
  return city
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Aliases de cidades (nomes noutras línguas) para a chave canónica. */
const CITY_ALIASES: Record<string, string> = {
  lisbon: "lisboa",
  lisbonne: "lisboa",
  lissabon: "lisboa",
  londres: "london",
  london: "london",
  rome: "roma",
  rom: "roma",
  milan: "milano",
  milao: "milano",
  mailand: "milano",
  amesterdao: "amsterdam",
  amsterdao: "amsterdam",
  berlim: "berlin",
  paris: "paris",
  madri: "madrid",
  barcelone: "barcelona",
};

export function resolveCityKey(city: string): string {
  const key = cityKey(city);
  return CITY_ALIASES[key] ?? key;
}

/** Valida um URL de recurso: apenas https, sem credenciais, host plausível. */
export function isValidResourceUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  if (url.username || url.password) return false;
  const host = url.hostname.toLowerCase();
  if (!host.includes(".") || host.endsWith(".")) return false;
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return false;
  return true;
}

export function resourceHost(raw: string): string | null {
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Domínios de revendedores: nunca podem ser marcados como oficiais. */
const RESELLER_HOSTS = [
  "omio.com",
  "getyourguide.com",
  "viator.com",
  "tiqets.com",
  "booking.com",
  "expedia.com",
  "klook.com",
  "civitatis.com",
  "rentalcars.com",
];

export function canBeOfficial(url: string): boolean {
  const host = resourceHost(url);
  if (!host) return false;
  return !RESELLER_HOSTS.some((r) => host === r || host.endsWith(`.${r}`));
}

export const STALE_AFTER_DAYS = 180;

export type Freshness = "verified" | "stale" | "unverified";

export function freshness(verifiedAt: string | null, now: number = Date.now()): Freshness {
  if (!verifiedAt) return "unverified";
  const ts = new Date(verifiedAt).getTime();
  if (Number.isNaN(ts)) return "unverified";
  return now - ts > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000 ? "stale" : "verified";
}

/**
 * Modos relevantes para uma viagem, deduzidos do transporte escolhido.
 * Determinístico e sem custo.
 */
export function relevantModes(transport: string): MobilityMode[] {
  switch (transport) {
    case "car":
      return ["parking", "tolls", "low_emission_zone", "car_rental", "transfer"];
    case "bike":
      return ["bike_share", "scooter", "transit_pass"];
    case "transit":
      return ["transit_pass", "transit_tickets", "transfer", "ferry"];
    default:
      return ["transit_pass", "transit_tickets", "bike_share", "scooter", "transfer"];
  }
}

/** Ordena e desduplica recursos: oficiais primeiro, depois prioridade e frescura. */
export function rankResources(
  resources: MobilityResource[],
  modes?: MobilityMode[],
  now?: number,
): MobilityResource[] {
  const allowed = modes && modes.length ? new Set<string>(modes) : null;
  const seen = new Set<string>();
  return resources
    .filter((r) => r.active && isValidResourceUrl(r.url))
    .filter((r) => (allowed ? allowed.has(r.mode) : true))
    .sort((a, b) => {
      if (a.official !== b.official) return a.official ? -1 : 1;
      if (a.priority !== b.priority) return a.priority - b.priority;
      const fa = freshness(a.verifiedAt, now) === "verified" ? 0 : 1;
      const fb = freshness(b.verifiedAt, now) === "verified" ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return a.providerName.localeCompare(b.providerName);
    })
    .filter((r) => {
      const k = `${r.mode}|${resourceHost(r.url)}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

/** Agrupa por modo mantendo a ordem dos modos pedidos. */
export function groupByMode(
  resources: MobilityResource[],
  modes: MobilityMode[],
): Array<{ mode: MobilityMode; label: string; warning?: string; items: MobilityResource[] }> {
  return modes
    .map((mode) => ({
      mode,
      label: MODE_LABEL[mode],
      ...(MODE_WARNING[mode] ? { warning: MODE_WARNING[mode] } : {}),
      items: resources.filter((r) => r.mode === mode),
    }))
    .filter((g) => g.items.length > 0);
}

/**
 * Fallback de pesquisa quando não há recurso curado. Não é apresentado como
 * oficial — serve apenas para o utilizador encontrar a fonte da cidade.
 */
export function searchFallback(city: string, mode: MobilityMode): string {
  const terms: Record<MobilityMode, string> = {
    transit_pass: "official public transport pass",
    transit_tickets: "official public transport tickets",
    car_rental: "official car rental",
    scooter: "e-scooter rules official",
    bike_share: "official bike share",
    parking: "official parking city council",
    tolls: "official motorway tolls",
    low_emission_zone: "low emission zone official rules",
    transfer: "official airport transfer",
    taxi_rideshare: "official taxi fares",
    ferry: "official ferry tickets",
  };
  return `https://www.google.com/search?q=${encodeURIComponent(`${city} ${terms[mode]}`)}`;
}

/** Chave de cache determinística (cidade + modos + versão do diretório). */
export function cacheKey(city: string, modes: MobilityMode[]): string {
  return `${resolveCityKey(city)}|${[...modes].sort().join(",")}`;
}
